/** 前台业务统一模拟学员。后续记录使用 id 关联学员，禁止按姓名或行号关联。 */
export type StudentStatus = '在读' | '试听' | '休学' | '退学';

export interface Student {
  readonly id: string;
  readonly studentNo: string;
  readonly name: string;
  readonly initials: string;
  readonly account: string;
  readonly gender: '男' | '女';
  readonly phone: string;
  readonly englishName: string;
  readonly identityNo: string;
  readonly cardNo: string;
  readonly school: string;
  readonly campus: string;
  readonly status: StudentStatus;
  readonly isFormal: boolean;
}

export const currentCampus = '岳麓校区';

type Seed = readonly [studentNo: string, name: string, initials: string, gender: Student['gender'], phone: string, status: StudentStatus, campus: string, school: string, englishName: string];

const seeds: readonly Seed[] = [
  ['SJ00124', '李梅', 'lm', '女', '185 8888 8888', '在读', currentCampus, '麓山实验小学', 'May'],
  ['SJ00125', '李娉', 'lp', '女', '185 6666 6666', '在读', currentCampus, '岳麓第一小学', 'Lily'],
  ['SJ00127', '李世海', 'lsh', '男', '185 2222 6666', '试听', currentCampus, '麓山实验小学', 'Leo'],
  ['SJ00126', '李书萍', 'lsp', '女', '185 5555 6666', '在读', currentCampus, '岳麓第二小学', 'Sophia'],
  ['SJ00128', '李雷', 'll', '男', '185 3333 6666', '休学', currentCampus, '岳麓第一小学', 'Ray'],
  ['SJ00129', '李冬梅', 'ldm', '女', '185 7777 6666', '退学', currentCampus, '麓山实验小学', 'Daisy'],
  ['SJ00130', '张子涵', 'zzh', '女', '186 0001 0001', '在读', currentCampus, '岳麓第二小学', 'Zoe'],
  ['SJ00131', '王梓轩', 'wzx', '男', '186 0001 0002', '在读', '梅溪湖校区', '梅溪湖实验小学', 'William'],
  ['SJ00132', '陈思源', 'csy', '男', '186 0001 0003', '在读', currentCampus, '岳麓第一小学', 'Sean'],
  ['SJ00133', '刘雨桐', 'lyt', '女', '186 0001 0004', '在读', currentCampus, '麓山实验小学', 'Yuna'],
  ['SJ00134', '赵晨曦', 'zcx', '女', '186 0001 0005', '在读', currentCampus, '岳麓第二小学', 'Chloe'],
  ['SJ00135', '周宇航', 'zyh', '男', '186 0001 0006', '试听', '梅溪湖校区', '梅溪湖实验小学', 'Ethan'],
  ['SJ00136', '吴欣怡', 'wxy', '女', '186 0001 0007', '在读', currentCampus, '岳麓第一小学', 'Wendy'],
  ['SJ00137', '徐浩然', 'xhr', '男', '186 0001 0008', '在读', currentCampus, '麓山实验小学', 'Henry'],
  ['SJ00138', '孙若溪', 'srx', '女', '186 0001 0009', '在读', '梅溪湖校区', '梅溪湖实验小学', 'Rosie'],
  ['SJ00139', '郑可馨', 'zkx', '女', '186 0001 0010', '在读', currentCampus, '岳麓第二小学', 'Kelly'],
  ['SJ00140', '杨嘉泽', 'yjz', '男', '186 0001 0011', '在读', currentCampus, '岳麓第一小学', 'Jay'],
  ['SJ00141', '何佳宁', 'hjn', '女', '186 0001 0012', '在读', '梅溪湖校区', '梅溪湖实验小学', 'Jenny'],
  ['SJ00142', '林沐阳', 'lmy', '男', '186 0001 0013', '在读', currentCampus, '麓山实验小学', 'Miles'],
  ['SJ00143', '黄诗涵', 'hsh', '女', '186 0001 0014', '在读', currentCampus, '岳麓第二小学', 'Hannah'],
];

/** 所有字段均为虚构演示信息；初始数据冻结，页面不得原地写入。 */
export const demoStudents: readonly Student[] = Object.freeze(seeds.map(([studentNo, name, initials, gender, phone, status, campus, school, englishName]) => Object.freeze({
  id: `student-${studentNo.toLowerCase()}`,
  studentNo, name, initials, gender, phone, status, campus, school, englishName,
  account: `demo_${studentNo.toLowerCase()}`,
  identityNo: '',
  // 两名原停课演示记录改为在读后仍保留原有空卡号。
  cardNo: status === '在读' && !['SJ00126', 'SJ00137'].includes(studentNo) ? `CARD${studentNo.slice(2)}` : '',
  isFormal: status !== '试听',
})));

export const normalizeStudentKeyword = (value: string) => value.replace(/\s/g, '').toLowerCase();

export function searchStudents(keyword: string): Student[] {
  const query = normalizeStudentKeyword(keyword);
  return demoStudents.filter((student) => [student.name, student.initials, student.studentNo, student.phone].some((value) => normalizeStudentKeyword(value).includes(query)));
}

export interface StudentFilters {
  keyword?: string;
  formal?: boolean;
  nonFormal?: boolean;
  includeOtherCampuses?: boolean;
}

export function filterStudents({ keyword = '', formal = true, nonFormal = true, includeOtherCampuses = true }: StudentFilters = {}): Student[] {
  return searchStudents(keyword).filter((student) => (student.isFormal ? formal : nonFormal) && (includeOtherCampuses || student.campus === currentCampus));
}

export function maskStudentPhone(phone: string): string {
  const digits = phone.replace(/\s/g, '');
  return `${digits.slice(0, 3)}****${digits.slice(-4)}`;
}
